import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

const BusinessContext = createContext();

export const BusinessProvider = ({ children }) => {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const updateBusinessId = async (businessId, businessName) => {
    try {
      console.log('=== DEBUG UPDATE BUSINESS ID FLOW ===', {
        currentBusinessId: user?.user_metadata?.businessId,
        newBusinessId: businessId,
        timestamp: new Date().toISOString()
      });

      // Solo actualizar si el business_id es diferente al actual
      if (user?.user_metadata?.businessId !== businessId) {
        console.log('Iniciando actualización de business ID');

        // 1. Actualizar perfil en Supabase
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            business_id: businessId,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (profileError) throw profileError;
        console.log('✅ Perfil actualizado en BD');

        // 2. Actualizar metadata del usuario
        const { data: { user: updatedUser }, error: userError } = await supabase.auth.updateUser({
          data: { 
            businessId: businessId,
            currentBusinessName: businessName,
            lastUpdated: new Date().toISOString()
          }
        });

        if (userError) throw userError;
        console.log('✅ Metadata actualizada:', {
          businessId: updatedUser.user_metadata.businessId,
          timestamp: new Date().toISOString()
        });

        // 3. Verificar cambios inmediatamente
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Estado de sesión post-actualización:', {
          metadata: session?.user?.user_metadata,
          timestamp: new Date().toISOString()
        });

        return updatedUser;
      } else {
        console.log('No se requiere actualización de business ID');
        return user;
      }
    } catch (err) {
      console.error('Error en updateBusinessId:', err);
      throw err;
    }
  };

  const fetchBusiness = async () => {
    try {
      console.log('=== DIAGNÓSTICO DETALLADO FETCH BUSINESS ===');
      
      // 1. Verificar sesión actual
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('❌ No hay sesión activa');
        setLoading(false);
        return;
      }

      console.log('1. Datos de sesión:', {
        userId: session.user.id,
        email: session.user.email,
        metadata: session.user.user_metadata,
        businessId: session.user.user_metadata?.businessId
      });

      // 2. MODIFICADO: Buscar miembros de negocio a través de business_members
      // Primero obtener todos los perfiles del usuario
      const { data: userProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', session.user.id);
        
      if (profilesError) throw profilesError;
      console.log('2. Perfiles del usuario:', userProfiles);
      
      if (!userProfiles || userProfiles.length === 0) {
        console.log('❌ Usuario no tiene perfiles');
        setLoading(false);
        return;
      }
      
      // Obtener las membresías de negocio usando los IDs de los perfiles
      const profileIds = userProfiles.map(profile => profile.id);
      const { data: businessMemberships, error: membershipsError } = await supabase
        .from('business_members')
        .select('business_id, profile_id, role, status')
        .in('profile_id', profileIds)
        .eq('status', 'active');
        
      if (membershipsError) throw membershipsError;
      console.log('2a. Membresías de negocios:', businessMemberships);
      
      // Si no hay membresías, no hay negocios activos
      if (!businessMemberships || businessMemberships.length === 0) {
        console.log('❌ Usuario no tiene membresías de negocio activas');
        setLoading(false);
        return;
      }

      // 3. Obtener detalles de los negocios
      const businessIds = businessMemberships.map(membership => membership.business_id);
      const { data: businesses, error: businessesError } = await supabase
        .from('businesses')
        .select('*')
        .in('id', businessIds);
      
      if (businessesError) throw businessesError;
      console.log('3. Negocios disponibles:', businesses);
      
      if (!businesses || businesses.length === 0) {
        console.log('❌ No se encontraron negocios');
        setLoading(false);
        return;
      }

      // 4. Determinar business_id activo
      let businessId = session.user.user_metadata?.businessId;
      let businessData = null;
      
      // Si hay un business_id en metadata, verificar que exista
      if (businessId) {
        businessData = businesses.find(b => b.id === businessId);
        console.log('4. Negocio por metadata:', businessData);
      }
      
      // Si no hay negocio por metadata, usar el primero disponible
      if (!businessData && businesses.length > 0) {
        businessData = businesses[0];
        businessId = businessData.id;
        console.log('4a. Usando primer negocio disponible:', businessData);
      }

      if (!businessData) {
        console.log('❌ No se encontró negocio activo');
        setLoading(false);
        return;
      }

      // 5. Actualizar metadatos del usuario si es necesario
      if (!session.user.user_metadata?.businessId || session.user.user_metadata.businessId !== businessId) {
        console.log('5. Actualizando metadata con businessId:', businessId);
        await updateBusinessId(businessId, businessData.name);
      } else {
        console.log('5. Metadata ya contiene el businessId correcto');
      }

      setBusiness(businessData);
      console.log('6. Estado final actualizado:', businessData);
    } catch (err) {
      console.error('Error en diagnóstico de fetchBusiness:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Debug effect para cambios en user
  useEffect(() => {
    console.log('=== DEBUG USER CHANGE EFFECT ===', {
      userId: user?.id,
      businessId: user?.user_metadata?.businessId,
      timestamp: new Date().toISOString()
    });
    
    if (user) {
      fetchBusiness();
    } else {
      setBusiness(null);
      setLoading(false);
    }
  }, [user]);

  // Debug effect para cambios en business
  useEffect(() => {
    console.log('=== DEBUG BUSINESS STATE CHANGE ===', {
      businessId: business?.id,
      businessName: business?.name,
      timestamp: new Date().toISOString()
    });
  }, [business]);

  // Mejorar el debug de auth subscription
  useEffect(() => {
    if (!user) return;

    console.log('=== DEBUG AUTH SUBSCRIPTION SETUP ===', {
      userId: user.id,
      timestamp: new Date().toISOString()
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('=== AUTH STATE CHANGE EVENT ===', {
        event,
        userId: session?.user?.id,
        businessId: session?.user?.user_metadata?.businessId,
        timestamp: new Date().toISOString()
      });

      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        console.log('Triggering fetchBusiness due to auth event:', event);
        fetchBusiness();
      }
    });

    return () => {
      console.log('Cleaning up auth subscription', {
        userId: user.id,
        timestamp: new Date().toISOString()
      });
      subscription?.unsubscribe();
    };
  }, [user]);

  const createBusiness = async (businessData) => {
    // Variables para seguimiento de recursos creados (para rollback)
    let createdBusinessId = null;
    let originalProfileBusinessId = null;
    let originalUserMetadata = null;

    try {
      console.log('=== INICIO FLUJO CREATE BUSINESS ===', {
        timestamp: new Date().toISOString()
      });
      
      // 1. Estado inicial
      const { data: { session: initialSession } } = await supabase.auth.getSession();
      console.log('1. Estado inicial:', {
        userMetadata: initialSession?.user?.user_metadata,
        businessId: initialSession?.user?.user_metadata?.businessId,
        email: initialSession?.user?.email
      });

      // Guardar estado original para posible rollback
      originalUserMetadata = initialSession?.user?.user_metadata;

      // 2. Verificar perfil actual
      const { data: initialProfile } = await supabase
        .from('profiles')
        .select('business_id, can_create_multiple_businesses')
        .eq('id', user.id)
        .single();
      console.log('2. Perfil inicial:', initialProfile);

      // Guardar business_id original para posible rollback
      originalProfileBusinessId = initialProfile?.business_id;

      // 3. Verificar negocios existentes
      const { data: existingBusinesses } = await supabase
        .from('businesses')
        .select('id, name')
        .eq('owner_id', user.id);
      console.log('3. Negocios existentes:', existingBusinesses);

      setLoading(true);
      setError(null);

      // Verificación de permisos
      if (initialProfile?.business_id && !initialProfile.can_create_multiple_businesses) {
        console.log('❌ Usuario intenta crear negocio sin permiso de múltiples');
        throw new Error('No tienes permiso para crear múltiples negocios.');
      }

      // 4. Crear nuevo negocio
      const businessId = uuidv4();
      createdBusinessId = businessId; // Guardar para posible rollback
      console.log('4. Nuevo business ID generado:', businessId);

      const { data: newBusiness, error: businessError } = await supabase
        .from('businesses')
        .insert([{
          id: businessId,
          name: businessData.name,
          description: businessData.description || `Negocio creado por ${user.email}`,
          active_team_members: JSON.stringify([user.email]),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          owner_id: user.id
        }])
        .select()
        .single();

      if (businessError) {
        console.error('Error creando negocio:', businessError);
        throw businessError;
      }
      
      console.log('5. Negocio creado exitosamente:', newBusiness);

      // 6. Actualizar perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          business_id: businessId,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Error actualizando perfil:', profileError);
        throw profileError;
      }

      console.log('6. Perfil actualizado con nuevo business_id');

      // 7. Crear entrada en business_members para el owner
      console.log('7. Creando entrada en business_members para el owner...');
      const { data: businessMember, error: businessMemberError } = await supabase
        .from('business_members')
        .insert([{
          business_id: businessId,
          profile_id: user.id,
          role: 'owner',
          status: 'active'
        }])
        .select();

      if (businessMemberError) {
        console.error('Error creando business member para el owner:', businessMemberError);
        throw businessMemberError;
      }

      console.log('✅ Business member creado para el owner:', businessMember);

      // 8. Actualizar metadata del usuario
      const { data: { user: updatedUser }, error: userError } = await supabase.auth.updateUser({
        data: { 
          businessId: businessId,
          currentBusinessName: businessData.name
        }
      });

      if (userError) {
        console.error('Error actualizando metadata:', userError);
        throw userError;
      }

      console.log('8. Metadata de usuario actualizada:', {
        businessId: updatedUser.user_metadata.businessId,
        currentBusinessName: updatedUser.user_metadata.currentBusinessName
      });

      // Después de crear el negocio
      console.log('=== VERIFICACIÓN POST-CREACIÓN ===', {
        newBusinessId: businessId,
        businessName: businessData.name,
        timestamp: new Date().toISOString()
      });

      // Verificar estado de la sesión después de todas las actualizaciones
      const { data: { session: finalSession } } = await supabase.auth.getSession();
      console.log('Estado final de la sesión:', {
        metadata: finalSession?.user?.user_metadata,
        timestamp: new Date().toISOString()
      });

      // Verificar que el contexto se actualice
      console.log('Actualizando estado local del contexto');
      setBusiness(newBusiness);
      
      return newBusiness;
    } catch (err) {
      console.error('Error en createBusiness:', err);
      
      // Implementar rollback manual
      try {
        console.log('Iniciando rollback manual debido a error:', err.message);
        
        // 1. Si se creó el negocio, eliminarlo
        if (createdBusinessId) {
          console.log('Eliminando negocio creado:', createdBusinessId);
          const { error: deleteError } = await supabase
            .from('businesses')
            .delete()
            .eq('id', createdBusinessId);
            
          if (deleteError) {
            console.error('Error en rollback al eliminar negocio:', deleteError);
          } else {
            console.log('✅ Negocio eliminado correctamente');
          }
        }
        
        // 2. Restaurar business_id original en el perfil
        if (originalProfileBusinessId !== null) {
          console.log('Restaurando business_id original en perfil:', originalProfileBusinessId);
          const { error: profileRestoreError } = await supabase
            .from('profiles')
            .update({ 
              business_id: originalProfileBusinessId,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
            
          if (profileRestoreError) {
            console.error('Error en rollback al restaurar perfil:', profileRestoreError);
          } else {
            console.log('✅ Perfil restaurado correctamente');
          }
        }
        
        // 3. Restaurar metadata original del usuario
        if (originalUserMetadata) {
          console.log('Restaurando metadata original del usuario');
          const { error: metadataRestoreError } = await supabase.auth.updateUser({
            data: originalUserMetadata
          });
          
          if (metadataRestoreError) {
            console.error('Error en rollback al restaurar metadata:', metadataRestoreError);
          } else {
            console.log('✅ Metadata restaurada correctamente');
          }
        }
        
        console.log('Rollback manual completado');
      } catch (rollbackError) {
        console.error('Error durante el rollback manual:', rollbackError);
      }
      
      // Re-lanzar el error original
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    business,
    loading,
    error,
    createBusiness,
    fetchBusiness
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness debe usarse dentro de un BusinessProvider');
  }
  return context;
};

export default BusinessContext; 